import React, { useState, useMemo, useCallback } from 'react';
import { Rating } from 'fronton-react';
import { ChevronRightIcon } from '@fronton/icons-react';
import { analyticsUtils } from 'utils/analytics-utils';
import { createElementKey } from 'utils';
import { Button } from '../../../button';
import { HoverPriceMediaCardSwiper } from '../../../price-media-card';
import { SwiperClass } from '../../../swiper';
import { Typography } from '../../../typography';
import { ReviewsCatalogModal } from '../review-catalog-modal';
import { ReviewsCatalogModalB } from '../review-catalog-modal-b';
import {
  reviewsCatalogItemImage,
  reviewsCatalogItem,
  reviewsCatalogList,
  reviewsCatalogItemContent,
  reviewsCatalogItemText,
  reviewsCatalogItemRating,
  reviewsCatalogItemTextWrapper,
  reviewsCatalogItemAddWrapper,
  reviewsCatalogButton,
} from './reviews-catalog-styles';

export type ReviewStructure = {
  imagesAlt: string;
  images: string[];
  bigImages: string[];
  rating: number;
  text: string;
  date: string;
  store?: string;
};

export const REVIEWS_PER_PAGE = 6;

type ReviewsCatalogProps = {
  elementBlock: string;
  modalButton?: React.ReactNode;
  newVariant?: boolean;
  reviewsList: ReviewStructure[];
}

export const ReviewsCatalog: React.FC<ReviewsCatalogProps> = ({ elementBlock, modalButton, newVariant = false, reviewsList }) => {
  const [reviewsPerPage, setReviewsPerPage] = useState<number>(REVIEWS_PER_PAGE);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeReview, setActiveReview] = useState<ReviewStructure | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);

  const CatalogModal = newVariant ? ReviewsCatalogModalB : ReviewsCatalogModal;

  const shownReviewsList = useMemo(() => reviewsList.slice(0, reviewsPerPage), [
    reviewsPerPage, reviewsList,
  ]);

  const isShowMoreBtnVisible = useMemo(() => reviewsPerPage < reviewsList.length, [
    reviewsPerPage, reviewsList,
  ]);

  const handleClickMoreBtn = useCallback(() => {
    setReviewsPerPage(reviewsPerPage + REVIEWS_PER_PAGE);
    analyticsUtils.clickButtonEvent(`${elementBlock} / Reviews`, 'Показать ещё', 'Отзывы клиентов');
  }, [reviewsPerPage, setReviewsPerPage, elementBlock]);

  const handleReviewClick = (review: ReviewStructure) => () => {
    setActiveReview(review);
    setIsModalOpen(true);
    analyticsUtils.clickButtonEvent(`${elementBlock} / Reviews`, 'Отзыв', 'Отзывы клиентов');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setThumbsSwiper(null);
  };

  return (
    <>
      <ul className={reviewsCatalogList}>
        {shownReviewsList.map((review, index) => (
          <li key={createElementKey('kitchen-review', index)}>
            <button className={reviewsCatalogItem} onClick={handleReviewClick(review)}>
              <HoverPriceMediaCardSwiper
                keyName="review-photo"
                photosName={review.imagesAlt}
                photosSrc={review.images}
                imageClass={reviewsCatalogItemImage}
              />
              <div className={reviewsCatalogItemContent}>
                <Rating className={reviewsCatalogItemRating} value={review.rating} size="l" />
                <div className={reviewsCatalogItemTextWrapper}>
                  <Typography
                    adaptive={false}
                    className={reviewsCatalogItemText}
                    variant="m"
                    size="body_long">
                    {review.text}
                  </Typography>
                  <ChevronRightIcon type="outline" size="22px" color="text-minor" />
                </div>
                <div className={reviewsCatalogItemAddWrapper}>
                  <Typography variant="caption" color="text-minor">
                    {review.date}
                  </Typography>
                  {review.store && (
                    <Typography variant="caption" color="text-minor">
                      {review.store}
                    </Typography>
                  )}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
      {isShowMoreBtnVisible && (
        <Button
          className={reviewsCatalogButton}
          onClick={handleClickMoreBtn}
          variant="secondary"
          invert
          rounded>
          Показать еще
        </Button>
      )}

      {activeReview && (
        <CatalogModal
          activeReview={activeReview}
          isOpen={isModalOpen}
          modalButton={modalButton}
          onClose={handleModalClose}
          setThumbsSwiper={setThumbsSwiper}
          thumbsSwiper={thumbsSwiper}
        />
      )}
    </>
  );
};
